/**
 * PaymentGatewayManager Component
 * 
 * Admin component for configuring payment gateways including:
 * - Stripe, PayPal, Bank Transfer, EcoCash, Manual payments
 * - Fee configuration
 * - Multi-currency support
 * - Subscription and installment toggles
 */
import React, { useState, useEffect } from 'react';
import { customPaymentAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Alert, AlertDescription } from '../components/ui/alert';
import CurrencySelect from './CurrencySelect';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  CreditCard, 
  Building2, 
  Smartphone, 
  Wallet, 
  DollarSign,
  Plus,
  Edit,
  Trash2,
  Settings,
  Check,
  X,
  AlertCircle,
  Loader2
} from 'lucide-react';

const GATEWAY_TYPES = [
  { id: 'stripe', name: 'Stripe', icon: CreditCard, description: 'Credit/Debit Cards' },
  { id: 'paypal', name: 'PayPal', icon: Wallet, description: 'PayPal Checkout' },
  { id: 'bank_transfer', name: 'Bank Transfer', icon: Building2, description: 'Wire/Bank Transfer' },
  { id: 'ecocash', name: 'EcoCash', icon: Smartphone, description: 'Zimbabwe Mobile Money' },
  { id: 'innbucks', name: 'InnBucks', icon: Smartphone, description: 'Zimbabwe InnBucks' },
  { id: 'onemoney', name: 'OneMoney', icon: Smartphone, description: 'NetOne Mobile Money' },
  { id: 'telecash', name: 'Telecash', icon: Smartphone, description: 'Telecel Mobile Money' },
  { id: 'mukuru', name: 'Mukuru', icon: Smartphone, description: 'Mukuru Money Transfer' },
  { id: 'mobile_money', name: 'M-Pesa', icon: Smartphone, description: 'East Africa Mobile Money' },
  { id: 'airtel_money', name: 'Airtel Money', icon: Smartphone, description: 'Airtel Mobile Money' },
  { id: 'manual', name: 'Cash/Manual', icon: DollarSign, description: 'Cash or Manual Payment' },
  { id: 'custom', name: 'Custom Gateway', icon: Plus, description: 'Add your own payment method' },
];

const CURRENCIES = ['USD', 'ZWL', 'ZAR', 'GBP', 'EUR', 'KES', 'NGN'];

const GATEWAY_CONFIG_FIELDS = {
  stripe: [
    { key: 'publishable_key', label: 'Publishable Key', type: 'text', required: true },
    { key: 'secret_key', label: 'Secret Key', type: 'password', required: true },
    { key: 'webhook_secret', label: 'Webhook Secret', type: 'password', required: false },
  ],
  paypal: [
    { key: 'client_id', label: 'Client ID', type: 'text', required: true },
    { key: 'client_secret', label: 'Client Secret', type: 'password', required: true },
    { key: 'sandbox', label: 'Sandbox Mode', type: 'boolean', required: false },
  ],
  bank_transfer: [
    { key: 'bank_name', label: 'Bank Name', type: 'text', required: true },
    { key: 'account_name', label: 'Account Name', type: 'text', required: true },
    { key: 'account_number', label: 'Account Number', type: 'text', required: true },
    { key: 'branch_code', label: 'Branch Code', type: 'text', required: false },
    { key: 'swift_code', label: 'SWIFT Code', type: 'text', required: false },
    { key: 'routing_number', label: 'Routing Number', type: 'text', required: false },
  ],
  ecocash: [
    { key: 'merchant_id', label: 'Merchant ID', type: 'text', required: true },
    { key: 'merchant_number', label: 'Merchant Number', type: 'text', required: true },
    { key: 'merchant_name', label: 'Merchant Name', type: 'text', required: true },
    { key: 'sandbox', label: 'Sandbox Mode', type: 'boolean', required: false },
  ],
  innbucks: [
    { key: 'wallet_id', label: 'InnBucks Wallet ID', type: 'text', required: true },
    { key: 'account_name', label: 'Account Name', type: 'text', required: true },
    { key: 'phone_number', label: 'Phone Number', type: 'text', required: true },
  ],
  onemoney: [
    { key: 'merchant_code', label: 'Merchant Code', type: 'text', required: true },
    { key: 'merchant_name', label: 'Merchant Name', type: 'text', required: true },
    { key: 'phone_number', label: 'Phone Number', type: 'text', required: true },
  ],
  telecash: [
    { key: 'merchant_number', label: 'Telecash Merchant Number', type: 'text', required: true },
    { key: 'merchant_name', label: 'Merchant Name', type: 'text', required: true },
    { key: 'phone_number', label: 'Phone Number', type: 'text', required: true },
  ],
  mukuru: [
    { key: 'recipient_name', label: 'Recipient Name', type: 'text', required: true },
    { key: 'recipient_phone', label: 'Recipient Phone', type: 'text', required: true },
    { key: 'recipient_id', label: 'Recipient ID', type: 'text', required: false },
    { key: 'pickup_location', label: 'Pickup Location', type: 'text', required: false },
  ],
  mobile_money: [
    { key: 'provider', label: 'Provider (M-Pesa, etc)', type: 'text', required: true },
    { key: 'phone_number', label: 'Business Phone', type: 'text', required: true },
    { key: 'business_name', label: 'Business Name', type: 'text', required: true },
    { key: 'paybill', label: 'Paybill Number', type: 'text', required: false },
    { key: 'till_number', label: 'Till Number', type: 'text', required: false },
  ],
  airtel_money: [
    { key: 'merchant_id', label: 'Merchant ID', type: 'text', required: true },
    { key: 'merchant_name', label: 'Merchant Name', type: 'text', required: true },
    { key: 'phone_number', label: 'Phone Number', type: 'text', required: true },
  ],
  manual: [
    { key: 'instructions', label: 'Payment Instructions', type: 'textarea', required: true },
    { key: 'contact_email', label: 'Contact Email', type: 'email', required: false },
    { key: 'contact_phone', label: 'Contact Phone', type: 'text', required: false },
  ],
  custom: [
    { key: 'gateway_name', label: 'Gateway Display Name', type: 'text', required: true },
    { key: 'instructions', label: 'Payment Instructions', type: 'textarea', required: true },
    { key: 'account_details', label: 'Account/Wallet Details', type: 'textarea', required: false },
    { key: 'verification_url', label: 'Verification URL (optional)', type: 'text', required: false },
    { key: 'verification_steps', label: 'Verification Steps', type: 'textarea', required: false },
    { key: 'contact_email', label: 'Contact Email', type: 'email', required: false },
    { key: 'contact_phone', label: 'Contact Phone', type: 'text', required: false },
    { key: 'requires_proof', label: 'Requires Payment Proof', type: 'boolean', required: false },
  ],
};

export default function PaymentGatewayManager({ eventId }) {
  const [gateways, setGateways] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingGateway, setEditingGateway] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    gateway: '',
    name: '',
    description: '',
    config: {},
    supported_currencies: ['USD'],
    fee_percentage: 0,
    fee_fixed: 0,
    min_amount: '',
    max_amount: '',
    supports_subscriptions: false,
    supports_installments: false,
  });
  
  useEffect(() => {
    loadGateways();
  }, [eventId]);
  
  const loadGateways = async () => {
    try {
      setLoading(true);
      const response = await customPaymentAPI.getGateways(eventId);
      setGateways(response.data || []);
    } catch (err) {
      setError('Failed to load payment gateways');
    } finally {
      setLoading(false);
    }
  };
  
  const handleOpenDialog = (gateway = null) => {
    if (gateway) {
      setEditingGateway(gateway);
      setFormData({
        gateway: gateway.gateway,
        name: gateway.name,
        description: gateway.description || '',
        config: gateway.config || {},
        supported_currencies: gateway.supported_currencies || ['USD'],
        fee_percentage: gateway.fee_percentage || 0,
        fee_fixed: gateway.fee_fixed || 0,
        min_amount: gateway.min_amount || '',
        max_amount: gateway.max_amount || '',
        supports_subscriptions: gateway.supports_subscriptions || false,
        supports_installments: gateway.supports_installments || false,
      });
    } else {
      setEditingGateway(null);
      setFormData({
        gateway: '',
        name: '',
        description: '',
        config: {},
        supported_currencies: ['USD'],
        fee_percentage: 0,
        fee_fixed: 0,
        min_amount: '',
        max_amount: '',
        supports_subscriptions: false,
        supports_installments: false,
      });
    }
    setDialogOpen(true);
  };
  
  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingGateway(null);
    setError(null);
  };
  
  const handleGatewayTypeChange = (type) => {
    const gatewayInfo = GATEWAY_TYPES.find(g => g.id === type);
    setFormData({
      ...formData,
      gateway: type,
      name: gatewayInfo?.name || '',
      description: gatewayInfo?.description || '',
      config: {},
    });
  };
  
  const handleConfigChange = (key, value) => {
    setFormData({
      ...formData,
      config: { ...formData.config, [key]: value },
    });
  };
  
  const handleCurrencyToggle = (currency) => {
    const currencies = [...formData.supported_currencies];
    const index = currencies.indexOf(currency);
    if (index >= 0) {
      currencies.splice(index, 1);
    } else {
      currencies.push(currency);
    }
    setFormData({ ...formData, supported_currencies: currencies });
  };
  
  const handleSubmit = async () => {
    // Validate required fields
    if (!formData.gateway || !formData.name) {
      setError('Please fill in all required fields');
      return;
    }
    
    // Validate required config fields
    const configFields = GATEWAY_CONFIG_FIELDS[formData.gateway] || [];
    for (const field of configFields) {
      if (field.required && !formData.config[field.key]) {
        setError(`${field.label} is required`);
        return;
      }
    }
    
    setSaving(true);
    setError(null);
    
    try {
      const payload = {
        ...formData,
        fee_percentage: parseFloat(formData.fee_percentage) || 0,
        fee_fixed: parseFloat(formData.fee_fixed) || 0,
        min_amount: formData.min_amount ? parseFloat(formData.min_amount) : null,
        max_amount: formData.max_amount ? parseFloat(formData.max_amount) : null,
      };
      
      if (editingGateway) {
        await customPaymentAPI.updateGateway(editingGateway.id, payload);
        setSuccess('Gateway updated successfully');
      } else {
        await customPaymentAPI.configureGateway(payload);
        setSuccess('Gateway created successfully');
      }
      
      handleCloseDialog();
      loadGateways();
      
      // Clear success after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      const message = err.response?.data?.detail || err.response?.data?.message || 'Failed to save gateway';
      setError(message);
    } finally {
      setSaving(false);
    }
  };
  
  const handleToggleActive = async (gateway) => {
    try {
      await customPaymentAPI.toggleGateway(gateway.id, !gateway.is_active);
      loadGateways();
    } catch (err) {
      setError('Failed to toggle gateway status');
    }
  };
  
  const handleDelete = async (gateway) => {
    if (!window.confirm(`Are you sure you want to delete ${gateway.name}?`)) {
      return;
    }
    
    try {
      await customPaymentAPI.deleteGateway(gateway.id);
      setSuccess('Gateway deleted successfully');
      loadGateways();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Failed to delete gateway');
    }
  };
  
  const renderConfigFields = () => {
    const fields = GATEWAY_CONFIG_FIELDS[formData.gateway] || [];
    
    return fields.map((field) => (
      <div key={field.key} className="space-y-1">
        <Label htmlFor={field.key}>
          {field.label}
          {field.required && <span className="text-red-500 ml-1">*</span>}
        </Label>
        
        {field.type === 'boolean' ? (
          <div className="flex items-center space-x-2">
            <Switch
              id={field.key}
              checked={formData.config[field.key] || false}
              onCheckedChange={(checked) => handleConfigChange(field.key, checked)}
            />
            <Label htmlFor={field.key} className="text-sm text-gray-500">
              {formData.config[field.key] ? 'Enabled' : 'Disabled'}
            </Label>
          </div>
        ) : field.type === 'textarea' ? (
          <textarea
            id={field.key}
            className="w-full p-2 border rounded-md min-h-[80px]"
            value={formData.config[field.key] || ''}
            onChange={(e) => handleConfigChange(field.key, e.target.value)}
            placeholder={field.label}
          />
        ) : (
          <Input
            id={field.key}
            type={field.type}
            value={formData.config[field.key] || ''}
            onChange={(e) => handleConfigChange(field.key, e.target.value)}
            placeholder={field.label}
          />
        )}
      </div>
    ));
  };
  
  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
          <span className="ml-2">Loading payment gateways...</span>
        </CardContent>
      </Card>
    );
  }
  
  return (
    <div className="space-y-4" data-testid="payment-gateway-manager">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">Payment Gateways</h2>
          <p className="text-sm text-gray-500">Configure payment methods for event registration</p>
        </div>
        <Button onClick={() => handleOpenDialog()} data-testid="add-gateway-btn">
          <Plus className="w-4 h-4 mr-2" />
          Add Gateway
        </Button>
      </div>
      
      {/* Success/Error Messages */}
      {success && (
        <Alert className="bg-green-50 border-green-200">
          <Check className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-800">{success}</AlertDescription>
        </Alert>
      )}
      
      {error && !dialogOpen && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      
      {/* Gateway List */}
      <div className="grid gap-4 md:grid-cols-2">
        {gateways.map((gateway) => {
          const GatewayIcon = GATEWAY_TYPES.find(g => g.id === gateway.gateway)?.icon || CreditCard;
          
          return (
            <Card key={gateway.id} className={!gateway.is_active ? 'opacity-60' : ''} data-testid={`gateway-card-${gateway.gateway}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <GatewayIcon className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-medium">{gateway.name}</h3>
                      <p className="text-sm text-gray-500">{gateway.description}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <Switch
                      checked={gateway.is_active}
                      onCheckedChange={() => handleToggleActive(gateway)}
                      data-testid={`toggle-${gateway.id}`}
                    />
                  </div>
                </div>
                
                <div className="mt-3 flex flex-wrap gap-1">
                  {gateway.supported_currencies?.map((currency) => (
                    <Badge key={currency} variant="outline" className="text-xs">
                      {currency}
                    </Badge>
                  ))}
                </div>
                
                <div className="mt-3 text-sm text-gray-500 space-y-1">
                  {(gateway.fee_percentage > 0 || gateway.fee_fixed > 0) && (
                    <p>Fee: {gateway.fee_percentage}%{gateway.fee_fixed > 0 ? ` + $${gateway.fee_fixed}` : ''}</p>
                  )}
                  <div className="flex gap-2">
                    {gateway.supports_subscriptions && (
                      <Badge variant="secondary" className="text-xs">Subscriptions</Badge>
                    )}
                    {gateway.supports_installments && (
                      <Badge variant="secondary" className="text-xs">Installments</Badge>
                    )}
                  </div>
                </div>
                
                <div className="mt-4 flex justify-end space-x-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => handleOpenDialog(gateway)}
                    data-testid={`edit-${gateway.id}`}
                  >
                    <Edit className="w-4 h-4 mr-1" />
                    Edit
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="text-red-600 hover:bg-red-50"
                    onClick={() => handleDelete(gateway)}
                    data-testid={`delete-${gateway.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
        
        {gateways.length === 0 && (
          <Card className="col-span-2">
            <CardContent className="py-8 text-center">
              <Settings className="w-12 h-12 mx-auto text-gray-400 mb-3" />
              <p className="text-gray-500">No payment gateways configured</p>
              <p className="text-sm text-gray-400">Click "Add Gateway" to get started</p>
            </CardContent>
          </Card>
        )}
      </div>
      
      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingGateway ? 'Edit Payment Gateway' : 'Add Payment Gateway'}
            </DialogTitle>
            <DialogDescription>
              Configure the payment gateway settings
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            
            {/* Gateway Type */}
            {!editingGateway && (
              <div className="space-y-2">
                <Label>Gateway Type <span className="text-red-500">*</span></Label>
                <Select value={formData.gateway} onValueChange={handleGatewayTypeChange}>
                  <SelectTrigger data-testid="gateway-type-select">
                    <SelectValue placeholder="Select a gateway type" />
                  </SelectTrigger>
                  <SelectContent>
                    {GATEWAY_TYPES.map((type) => (
                      <SelectItem key={type.id} value={type.id}>
                        <div className="flex items-center">
                          <type.icon className="w-4 h-4 mr-2" />
                          {type.name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {/* Name & Description */}
            <div className="space-y-2">
              <Label htmlFor="name">Display Name <span className="text-red-500">*</span></Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Credit Card Payment"
                data-testid="gateway-name-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Input
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Brief description for users"
              />
            </div>
            
            {/* Gateway-specific config */}
            {formData.gateway && (
              <div className="space-y-4 border-t pt-4">
                <h4 className="font-medium">Gateway Configuration</h4>
                {renderConfigFields()}
              </div>
            )}
            
            {/* Fee Configuration */}
            <div className="space-y-4 border-t pt-4">
              <h4 className="font-medium">Fee Configuration</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="fee_percentage">Fee Percentage (%)</Label>
                  <Input
                    id="fee_percentage"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.fee_percentage}
                    onChange={(e) => setFormData({ ...formData, fee_percentage: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="fee_fixed">Fixed Fee ($)</Label>
                  <Input
                    id="fee_fixed"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.fee_fixed}
                    onChange={(e) => setFormData({ ...formData, fee_fixed: e.target.value })}
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="min_amount">Minimum Amount</Label>
                  <Input
                    id="min_amount"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.min_amount}
                    onChange={(e) => setFormData({ ...formData, min_amount: e.target.value })}
                    placeholder="No minimum"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="max_amount">Maximum Amount</Label>
                  <Input
                    id="max_amount"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.max_amount}
                    onChange={(e) => setFormData({ ...formData, max_amount: e.target.value })}
                    placeholder="No maximum"
                  />
                </div>
              </div>
            </div>
            
            {/* Currencies */}
            <div className="space-y-2 border-t pt-4">
              <Label>Supported Currencies</Label>
              <div className="flex flex-wrap gap-2">
                {[...new Set([...CURRENCIES, ...formData.supported_currencies])].map((currency) => (
                  <Badge
                    key={currency}
                    variant={formData.supported_currencies.includes(currency) ? 'default' : 'outline'}
                    className="cursor-pointer"
                    onClick={() => handleCurrencyToggle(currency)}
                  >
                    {currency}
                    {formData.supported_currencies.includes(currency) && (
                      <Check className="w-3 h-3 ml-1" />
                    )}
                  </Badge>
                ))}
              </div>
              <div className="w-56">
                <CurrencySelect
                  value=""
                  placeholder="Add any world currency…"
                  onChange={(code) => {
                    if (!formData.supported_currencies.includes(code)) handleCurrencyToggle(code);
                  }}
                  showHomeHint={false}
                  testId="gateway-add-currency"
                />
              </div>
            </div>
            
            {/* Payment Options */}
            <div className="space-y-4 border-t pt-4">
              <h4 className="font-medium">Payment Options</h4>
              <div className="flex items-center justify-between">
                <div>
                  <Label>Support Subscriptions</Label>
                  <p className="text-sm text-gray-500">Allow recurring payments</p>
                </div>
                <Switch
                  checked={formData.supports_subscriptions}
                  onCheckedChange={(checked) => setFormData({ ...formData, supports_subscriptions: checked })}
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <Label>Support Installments</Label>
                  <p className="text-sm text-gray-500">Allow split payments</p>
                </div>
                <Switch
                  checked={formData.supports_installments}
                  onCheckedChange={(checked) => setFormData({ ...formData, supports_installments: checked })}
                />
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={handleCloseDialog}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={saving} data-testid="save-gateway-btn">
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  {editingGateway ? 'Update Gateway' : 'Create Gateway'}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
